import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQueryErrorResetBoundary, useSuspenseQuery} from '@github-ui/react-query'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useState} from 'react'

import {LABELS} from '../constants/labels'
import {editHistoryDialogQueryKey, fetchEditHistoryDialog} from '../fetch-edit-history-dialog'
import {CouldNotFindFallbackError} from './CouldNotFindFallbackError'
import styles from './EditHistoryDialog.module.css'
import {ActorOperationDetails, EditHistoryDialogHeader} from './EditHistoryDialogHeader'
import {GroupedTextDiffViewer} from './GroupedTextDiffViewer'

type EditHistoryDialogProps = {
  userContentEditId: string
  onClose: () => void
}

export const EditHistoryDialog = ({userContentEditId, onClose}: EditHistoryDialogProps) => {
  return (
    <Dialog
      renderBody={() => <EditContent userContentEditId={userContentEditId} />}
      onClose={onClose}
      title={LABELS.editHistory.viewingEditTitle}
      width="xlarge"
      height="auto"
      className={styles.EditHistoryDialogContainer}
    />
  )
}

const EditContent = ({userContentEditId}: Pick<EditHistoryDialogProps, 'userContentEditId'>) => {
  const {reset} = useQueryErrorResetBoundary()
  const [retryKey, setRetryKey] = useState(0)
  const retry = () => {
    reset()
    setRetryKey(key => key + 1)
  }

  return (
    <ErrorBoundary
      key={retryKey}
      boundaryName="EditHistoryDialog"
      fallback={<CouldNotFindFallbackError retry={retry} />}
    >
      <Suspense fallback={<EditContentLoading />}>
        <EditContentInternal userContentEditId={userContentEditId} />
      </Suspense>
    </ErrorBoundary>
  )
}

const EditContentInternal = ({userContentEditId}: Pick<EditHistoryDialogProps, 'userContentEditId'>) => {
  const {data} = useSuspenseQuery({
    queryKey: editHistoryDialogQueryKey(userContentEditId),
    queryFn: ({signal}) => fetchEditHistoryDialog(userContentEditId, signal),
  })

  // This should only be possible to happen on the first creation of an issue body
  const emptyDiff = !data.diffBefore && !data.diff

  return (
    <div className={styles.editHistoryContentScrollable}>
      <EditHistoryDialogHeader data={data} userContentEditId={userContentEditId} />
      <div className={styles.contentViewerContainer}>
        {!data.deletedAt && !emptyDiff && (
          <GroupedTextDiffViewer before={data.diffBefore ?? undefined} after={data.diff ?? undefined} />
        )}
        {!data.deletedAt && emptyDiff && <span className={styles.mutedInfoText}>{LABELS.editHistory.emptyEdit}</span>}
        {data.deletedAt && (
          <ActorOperationDetails
            login={data.deletedBy?.login}
            details={LABELS.editHistory.deletedThisRevision}
            time={data.deletedAt}
            className={styles.mutedInfoText}
          />
        )}
      </div>
    </div>
  )
}

function EditContentLoading() {
  return (
    <div className={styles.loadingSkeletonContainer}>
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
    </div>
  )
}
